import React, { useEffect, useRef, useState } from 'react';
import NukaCarousel from '@stitch-fix/nuka-carousel';
import { Controlled as ControlledZoom } from 'react-medium-image-zoom';
import classNames from 'classnames';
import { ResizeObserver } from '@juggle/resize-observer';
import { getBreakpoints } from '../_internal/screenSizes';
import useMedia from '../_internal/useMedia';
import Box from '../Box';
import CloudinaryImage, { CloudinaryImageProps } from '../CloudinaryImage';
import Placeholder from '../Placeholder';
import FloatingAction from '../FloatingAction';
import IconArrowDown16 from '../icons/IconArrowDown16';
import Indicator from './Indicator';
import Text from '../Text';
import ThumbnailNavigation from './ThumbnailNavigation';
import IconButton from '../IconButton';
import IconChevronLeft16 from '../icons/IconChevronLeft16';
import IconChevronRight16 from '../icons/IconChevronRight16';
import styles from './pager.module.scss';
import './pager.overrides.scss';

interface ImageProps extends CloudinaryImageProps {
  /**
   * Horizontal alignment of the CloudinaryImage within the Pager slide container.
   */
  horizontalAlignment?: 'left' | 'right';
}
type ThumbnailImageProps = Omit<ImageProps, 'sizes'>;

export interface PagerProps {
  /**
   * Content displayed within the Pager area. Could be used for a `Save` bookmark.
   * */
  actionContent?: React.ReactNode;
  /**
   * Floating action button displayed on the first slide.
   * */
  jumpLink?: {
    text: string;
    onClick: () => void;
  };
  /**
   * Callback function when the slide changes.
   */
  onSlideChange?: (slideIndex: number) => void;
  /**
   * Slides rendered by the Pager. If an empty array is passed, the component renders a loading state.
   */
  slides: {
    /**
     * The main slide content. Can be a ReactNode for places we want
     * something other than an image.
     * */
    main: ImageProps | React.ReactNode;
    /**
     * Optional thumbnail content if content differs; the fallback for
     * thumbnails will be the main slide content.
     * */
    thumbnail?: ThumbnailImageProps | React.ReactNode;
    /**
     *  Hides `actionContent` on the slide. Typically used when the `main` is a `ReactNode`, like for a `ShoppableOutfit`
     * */
    hideActionContent?: boolean;
    /**
     * Additional text information to display on the bottom of the Pager. Rendered inside of a `Text` element.
     */
    caption?: React.ReactNode;
  }[];
}

const isImage = (
  element: React.ReactNode | ImageProps | ThumbnailImageProps,
): element is ImageProps | ThumbnailImageProps =>
  'mediaPath' in (element as ImageProps);

/**
 * Pagers are a variant of a carousel that only present one slide or “page” at a time.
 * We use pagers when it’s important to focus our clients on each slide‘s content,
 * whereas slider carousels present an assortment of content.
 */
const Pager = ({
  actionContent,
  jumpLink,
  onSlideChange,
  slides,
}: PagerProps) => {
  const [hideThumbnails, setHideThumbnails] = useState(true);
  // Because the carousel width is determined without thumbnails on initial render, we need
  // to re-render the carousel after we update the hideThumbnails state.
  const [shouldRenderCarousel, setShouldRenderCarousel] = useState(false);
  const [selectedSlide, setSelectedSlide] = useState(0);
  const [isZoomedIndex, setIsZoomedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const smallBreakpoint = getBreakpoints().sm;
  const isLoading = slides.length === 0;
  const prefersReducedMotion = useMedia('(prefers-reduced-motion)');

  useEffect(() => {
    const pagerContainer = containerRef.current;

    if (!pagerContainer) {
      return undefined;
    }

    const observer = new ResizeObserver(([firstEntry]) => {
      const pagerWidth = firstEntry.contentRect.width;

      setHideThumbnails(pagerWidth <= smallBreakpoint);
      setShouldRenderCarousel(true);
    });

    observer.observe(pagerContainer);

    return () => {
      observer.unobserve(pagerContainer);
    };
  }, [containerRef, smallBreakpoint]);

  const handleSlideChange = (index: number) => {
    if (index !== selectedSlide) {
      setSelectedSlide(index);
      onSlideChange?.(index);
    }
  };

  const updateZoomIndex = (isZoomed: boolean) => {
    setIsZoomedIndex(isZoomed ? selectedSlide : -1);
  };

  const slideElements = slides.map((slide, index) => {
    const isImageZoomed = isZoomedIndex === index;

    const element = isImage(slide.main) ? (
      <Box
        data-testid="slide-cloudinary-image"
        className={classNames(
          styles['cloudinary-image'],
          // disables the zoom button on slides that are not in view
          {
            [styles['disable-zoom-button']]: selectedSlide !== index,
          },
        )}
      >
        <ControlledZoom
          isZoomed={isImageZoomed}
          onZoomChange={updateZoomIndex}
          wrapStyle={{ width: '100%', height: '100%', overflow: 'hidden' }}
          openText={`Zoom into ${slide.main.alt}`}
        >
          <CloudinaryImage
            objectPosition={slide.main?.horizontalAlignment}
            {...slide.main}
            sizes={isImageZoomed ? { sm: 560, lg: 700 } : slide.main.sizes}
          />
        </ControlledZoom>
      </Box>
    ) : (
      slide.main
    );

    return (
      // eslint-disable-next-line react/no-array-index-key
      <Box className={styles['slide-container']} key={index}>
        <Box className={styles['slide-content']}>{element}</Box>
      </Box>
    );
  });

  const thumbnails = slides.map(slide => {
    const thumbnail = slide.thumbnail || slide.main;

    return isImage(thumbnail) ? (
      <Box className={styles['cloudinary-image']}>
        <CloudinaryImage
          {...thumbnail}
          sizes={{ sm: 88 }}
          objectPosition={thumbnail?.horizontalAlignment}
        />
      </Box>
    ) : (
      thumbnail
    );
  });

  const displayActionContent =
    actionContent && !slides[selectedSlide]?.hideActionContent && !isLoading;

  const displayCaption = !isLoading && slides.some(slide => slide.caption);

  return (
    <Box
      className={classNames({
        [styles['hide-thumbnails']]: hideThumbnails,
      })}
    >
      <Box ref={containerRef} className={styles.container}>
        {displayActionContent && (
          <div
            className={styles['action-content']}
            data-testid="action-content"
          >
            {actionContent}
          </div>
        )}
        {!hideThumbnails && (
          <ThumbnailNavigation
            thumbnails={thumbnails}
            selectedSlide={selectedSlide}
            setSelectedSlide={handleSlideChange}
          />
        )}

        <Box className={styles.main} data-testid="slides">
          {hideThumbnails && (
            <>
              <Box className={`${styles.control} ${styles.previous}`}>
                <IconButton
                  variant="knockout"
                  aria-label="Previous slide"
                  onClick={() => handleSlideChange(selectedSlide - 1)}
                  disabled={selectedSlide === 0}
                >
                  <IconChevronLeft16 />
                </IconButton>
              </Box>
              <Box className={`${styles.control} ${styles.next}`}>
                <IconButton
                  variant="knockout"
                  aria-label="Next slide"
                  onClick={() => handleSlideChange(selectedSlide + 1)}
                  disabled={selectedSlide === slides.length - 1}
                >
                  <IconChevronRight16 />
                </IconButton>
              </Box>
            </>
          )}
          {shouldRenderCarousel && !isLoading && (
            <NukaCarousel
              afterSlide={handleSlideChange}
              cellSpacing={0}
              disableAnimation={prefersReducedMotion}
              disableEdgeSwiping
              enableKeyboardControls
              slideIndex={selectedSlide}
              slidesToShow={1}
              withoutControls
              data-testid="slides"
            >
              {slideElements}
            </NukaCarousel>
          )}
          {isLoading && (
            <Box
              className={styles['slide-container']}
              data-testid="slide-placeholder"
            >
              <Box className={styles['slide-content']}>
                <Placeholder width="100%" height="100%" variant="rect" />
              </Box>
            </Box>
          )}
          {jumpLink && !isLoading && (
            <Box
              className={classNames(styles['jump-link'], {
                hidden: selectedSlide !== 0,
              })}
            >
              <FloatingAction
                mb={hideThumbnails ? 1.75 : 0.75}
                hideLabel={false}
                icon={<IconArrowDown16 />}
                onClick={jumpLink.onClick}
              >
                {jumpLink.text}
              </FloatingAction>
            </Box>
          )}
          {hideThumbnails && (
            <Box className={styles.indicator}>
              <Indicator
                numberOfSlides={slides.length}
                selectedSlide={selectedSlide}
              />
            </Box>
          )}
        </Box>
      </Box>
      {displayCaption && (
        <Box className={styles.caption} data-testid="caption">
          <Text setting="body-small" as="p" align="center">
            {slides[selectedSlide]?.caption}
          </Text>
        </Box>
      )}
    </Box>
  );
};

export default Pager;
